import { useEffect, useState, useCallback } from "react";
import { Calendar, Clock, Video, X, Loader2, CheckCircle2, ArrowRight, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function OfficeHoursModal({ open, onClose, groups, config, onBooked }) {
  const { user } = useAuth();
  const [dayIdx, setDayIdx] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "" });
  const [step, setStep] = useState("pick"); // pick | confirm | booking | done
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      // reset on close
      setTimeout(() => {
        setStep("pick");
        setSelectedSlot(null);
        setResult(null);
      }, 200);
    }
  }, [open]);

  if (!open) return null;

  const currentDay = groups[dayIdx];
  const slots = currentDay?.slots || [];

  const submit = async (e) => {
    e?.preventDefault();
    if (!selectedSlot) return;
    if (!form.name || !form.email || !form.topic) {
      toast.error("Please fill name, email and topic");
      return;
    }
    setStep("booking");
    try {
      const { data } = await api.post("/bookings/founder", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        topic: form.topic,
        slot_iso: selectedSlot.iso,
      });
      setResult(data);
      setStep("done");
      onBooked?.(data);
      toast.success("Booked! Confirmation sent to your email.");
    } catch (err) {
      toast.error(formatApiError(err));
      setStep("confirm");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.meeting_url);
      toast.success("Meeting link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="oh-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0F172A] shadow-2xl shadow-black/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400">
              <Calendar className="h-4 w-4 text-[#0A101D]" />
            </div>
            <div>
              <div className="text-sm font-bold text-white">Book a 15-min call with {config?.host_name}</div>
              <div className="text-xs text-white/55">{config?.host_title} · {config?.work_days} · {config?.work_hours_label}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg text-white/60 hover:bg-white/5 hover:text-white"
            data-testid="oh-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto">
          {step === "pick" && (
            <div className="p-5">
              {groups.length === 0 ? (
                <div className="py-10 text-center text-sm text-white/55">No open slots in the next 7 days. Try WhatsApp instead.</div>
              ) : (
                <>
                  {/* Day picker */}
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <button
                      onClick={() => setDayIdx((i) => Math.max(0, i - 1))}
                      disabled={dayIdx === 0}
                      className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/10 disabled:opacity-30"
                      data-testid="oh-day-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-base font-bold text-white" data-testid="oh-day-label">{currentDay?.date_label}</div>
                      <div className="text-[11px] text-white/45 uppercase tracking-wider">{slots.length} slots · IST</div>
                    </div>
                    <button
                      onClick={() => setDayIdx((i) => Math.min(groups.length - 1, i + 1))}
                      disabled={dayIdx >= groups.length - 1}
                      className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/10 disabled:opacity-30"
                      data-testid="oh-day-next"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Slot grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="oh-slot-grid">
                    {slots.map((s) => (
                      <button
                        key={s.iso}
                        onClick={() => { setSelectedSlot(s); setStep("confirm"); }}
                        disabled={!s.available}
                        className={`rounded-lg border px-2 py-2.5 text-xs font-semibold transition-all ${
                          s.available
                            ? "border-white/15 bg-white/[0.04] text-white hover:border-cyan-400/60 hover:bg-cyan-500/10 hover:text-cyan-200"
                            : "border-white/5 bg-white/[0.02] text-white/30 line-through cursor-not-allowed"
                        }`}
                        data-testid={`oh-slot-${s.iso}`}
                      >
                        {s.time_label.replace(" IST", "")}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {step === "confirm" && selectedSlot && (
            <form onSubmit={submit} className="p-5 space-y-3" data-testid="oh-confirm-form">
              <button
                type="button"
                onClick={() => { setSelectedSlot(null); setStep("pick"); }}
                className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-1"
                data-testid="oh-back"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Pick another slot
              </button>
              <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/[0.08] px-4 py-3 flex items-center gap-3">
                <Clock className="h-5 w-5 text-cyan-300" />
                <div>
                  <div className="text-sm font-bold text-white">{selectedSlot.date_label} · {selectedSlot.time_label}</div>
                  <div className="text-[11px] text-white/55">{config?.duration_min}-min Zoom-style call via Jitsi · No download required</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs text-white/60">
                  Name *
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                    data-testid="oh-name" />
                </label>
                <label className="text-xs text-white/60">
                  Email *
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                    data-testid="oh-email" />
                </label>
                <label className="text-xs text-white/60 sm:col-span-2">
                  Phone (optional)
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91…"
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
                    data-testid="oh-phone" />
                </label>
                <label className="text-xs text-white/60 sm:col-span-2">
                  What would you like to discuss? *
                  <textarea required rows={3} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    placeholder="Brief description so Yadav can prep ahead…"
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none resize-none"
                    data-testid="oh-topic" />
                </label>
              </div>
              <button
                type="submit"
                disabled={step === "booking"}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 disabled:opacity-50 px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_-8px_rgba(34,211,238,0.6)]"
                data-testid="oh-submit"
              >
                {step === "booking" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                Confirm Booking — Free
              </button>
              <p className="text-[10px] text-center text-white/40">By booking, you agree to receive a confirmation email with the meeting link.</p>
            </form>
          )}

          {step === "booking" && (
            <div className="p-10 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-300 mx-auto" />
              <div className="mt-3 text-sm text-white/70">Confirming your booking…</div>
            </div>
          )}

          {step === "done" && result && (
            <div className="p-5 text-center" data-testid="oh-done">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15 border border-emerald-400/40">
                <CheckCircle2 className="h-7 w-7 text-emerald-300" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-white">You&apos;re on Yadav&apos;s calendar!</h3>
              <p className="mt-1 text-sm text-white/65">
                {new Date(result.slot_iso).toLocaleString("en-IN", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST · 15-minute call
              </p>
              <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/55">Meeting link</div>
                <div className="mt-1 flex items-center gap-2">
                  <a href={result.meeting_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-300 hover:text-cyan-200 truncate font-mono" data-testid="oh-link">{result.meeting_url}</a>
                  <button onClick={copyLink} className="grid h-7 w-7 place-items-center rounded-md text-white/55 hover:bg-white/5 hover:text-white" data-testid="oh-copy" aria-label="Copy link">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <a
                href={result.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_-8px_rgba(16,185,129,0.55)]"
                data-testid="oh-join-now"
              >
                <Video className="h-4 w-4" /> Open Meeting Room
              </a>
              <p className="mt-3 text-[11px] text-white/45">A confirmation email is on its way to {result.email}.</p>
              <button
                onClick={onClose}
                className="mt-4 text-xs text-white/55 hover:text-white"
                data-testid="oh-modal-close-done"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FounderOfficeHours() {
  const [config, setConfig] = useState(null);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/bookings/founder/config"),
        api.get("/bookings/founder/slots?days=7"),
      ]);
      setConfig(c.data);
      setGrouped(s.data.grouped || []);
    } catch (e) {
      // silently degrade — widget hides
      setGrouped([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Flatten next 3 available slots from grouped
  const next3 = (grouped || [])
    .flatMap((g) => g.slots.map((s) => ({ ...s, _date: g.date_label })))
    .filter((s) => s.available)
    .slice(0, 3);

  if (loading) return null;
  if (!next3.length) return null; // nothing to show — hide gracefully

  return (
    <section className="container-x mt-5" data-testid="founder-office-hours">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0F172A] via-[#0A101D] to-[#0F172A] backdrop-blur-md p-5">
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-12 -bottom-12 h-36 w-36 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500/30 to-cyan-400/15 border border-cyan-400/30">
              <Calendar className="h-5 w-5 text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 border border-blue-400/40 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-blue-200">
                  Founder Office Hours
                </span>
                <span className="text-[10px] text-emerald-300 font-semibold uppercase tracking-wider">Free · 15 min</span>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white truncate">Book a private 15-min call with Yadav</h3>
              <p className="text-xs sm:text-sm text-white/60 mt-0.5">No sales pitch — just a real conversation about your IT challenges.</p>
            </div>
          </div>

          {/* Next 3 slots */}
          <div className="flex flex-wrap items-center gap-2" data-testid="oh-next-slots">
            {next3.map((s) => (
              <button
                key={s.iso}
                onClick={() => setModalOpen(true)}
                className="rounded-lg border border-white/10 bg-white/[0.04] hover:border-cyan-400/50 hover:bg-cyan-500/10 hover:text-cyan-200 px-3 py-2 text-xs font-semibold text-white transition-all"
                data-testid={`oh-quick-slot-${s.iso}`}
              >
                <div className="text-[10px] text-white/55 mb-0.5">{s._date}</div>
                <div>{s.time_label.replace(" IST", "")}</div>
              </button>
            ))}
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 active:scale-[0.98] px-4 py-2.5 text-xs font-bold text-white shadow-[0_8px_24px_-8px_rgba(34,211,238,0.55)] shrink-0"
              data-testid="oh-open-modal"
            >
              See all slots <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <OfficeHoursModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        groups={grouped}
        config={config}
        onBooked={() => { refresh(); }}
      />
    </section>
  );
}
